import { Component, computed, inject, input, ChangeDetectionStrategy, resource } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { WeatherService } from '@services/weather.service';
import { YearData, ProcessedDailyWeather } from '@interfaces/weather.interface';
import { weatherIconMap, weatherLabelMap } from '@utils/weather-icons';
import { catchError, distinctUntilChanged, filter, map, startWith, switchMap } from 'rxjs/operators';
import { lastValueFrom } from 'rxjs';

@Component({
	selector: 'app-weather',
	imports: [],
	templateUrl: './weather.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrl: './weather.component.scss'
})
export class WeatherComponent {
	readonly year = input<number | null>(null);
	readonly month = input<number | null>(null);
	readonly day = input<number | null>(null);
	private ws = inject(WeatherService);

	/** Builds "YYYY-MM-DD" with zero-padding */
	readonly date = computed((): string | null => {
		const year = this.year();
		const month = this.month();
		const day = this.day();
		if (year == null || month == null || day == null) return null;
		const m = String(month).padStart(2, '0');
		const d = String(day).padStart(2, '0');
		return `${year}-${m}-${d}`;
	});

	readonly weatherResource = resource({
		params: () => this.date(),
		loader: ({ params: date }) => {
			if (!date) return Promise.resolve(null);
			return lastValueFrom(this.ws.getWeatherForDate(date));
		}
	});

	private readonly viewState = computed(() => {
		if (this.weatherResource.isLoading()) {
			return { label: 'Loading', iconName: 'cloud_sync', avgTemp: null as number | null };
		}

		const data = this.weatherResource.value();
		if (this.weatherResource.error() || !data) {
			return { label: 'Error loading weather', iconName: 'cloud_alert', avgTemp: null as number | null };
		}
		const avgTemp = Number(((data.tMax + data.tMin) / 2).toFixed(1));
		const code = data.dominantCode;

		if (code === null) {
			return { label: 'No weather data', iconName: 'cloud_alert', avgTemp: null as number | null };
		}

		let label = weatherLabelMap[code] || 'Unknown';
		let iconName = weatherIconMap[code] || 'cloud_alert';

		if (data.precipitationSum >= 0.1) {
			const p = data.precipitationSum;
			const isSnow = [71, 73, 75, 77, 85, 86].includes(code);
			const isStorm = [95, 96, 99].includes(code);

			let prefix = '';
			let icon = '';

			if (p >= 151) {
				prefix = 'Extremely Heavy';
				icon = isSnow ? 'weather_snowy' : (isStorm ? 'thunderstorm' : 'rainy_heavy');
			} else if (p >= 71) {
				prefix = 'Very Heavy';
				icon = isSnow ? 'weather_snowy' : (isStorm ? 'thunderstorm' : 'rainy_heavy');
			} else if (p >= 31) {
				prefix = 'Heavy';
				icon = isSnow ? 'weather_snowy' : (isStorm ? 'thunderstorm' : 'rainy_heavy');
			} else if (p >= 11) {
				prefix = 'Moderate';
				icon = isSnow ? 'snowing' : (isStorm ? 'thunderstorm' : 'rainy');
			} else if (p >= 1) {
				prefix = 'Light';
				icon = isSnow ? 'snowing' : (isStorm ? 'thunderstorm' : 'rainy_light');
			} else {
				prefix = 'Very Light';
				icon = isSnow ? 'snowing' : (isStorm ? 'thunderstorm' : 'rainy_light');
			}

			const baseType = isSnow ? 'Snow' : (isStorm ? 'Thunderstorm' : 'Rain');
			label = `${prefix} ${baseType}`;
			iconName = icon;
		}

		return { label, iconName, avgTemp };
	});

	readonly label = computed(() => this.viewState().label);
	readonly iconName = computed(() => this.viewState().iconName);
	readonly avgTemp = computed(() => this.viewState().avgTemp);
}
